import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { TrendingUp, TrendingDown } from 'lucide-react'
import {
	formatConversionRate,
	formatConversionAmount,
} from '../../utils/formatConversionData'

import type { ChannelAnalytics } from '../../types'

interface OverallPerformanceCardProps {
	analytics: ChannelAnalytics | undefined
	title?: string
	subtitle?: string
	isLoading?: boolean
}

export default function OverallPerformanceCard({
	analytics,
	title = 'ประสิทธิภาพรวม (SMS + LINE)',
	subtitle,
	isLoading,
}: OverallPerformanceCardProps) {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-[200px] animate-pulse rounded bg-muted" />
				</CardContent>
			</Card>
		)
	}

	if (!analytics) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="text-center text-muted-foreground">ไม่มีข้อมูล</p>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>{title}</CardTitle>
				<p className="text-sm text-muted-foreground">
					{subtitle ||
						`ผู้รับทั้งหมด: ${analytics.totalRecipients.toLocaleString('th-TH')} คน | ส่งสำเร็จ: ${analytics.successfulDeliveries.toLocaleString('th-TH')} คน`}
				</p>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-[140px]"></TableHead>
								<TableHead className="text-center">1 วัน</TableHead>
								<TableHead className="text-center">3 วัน</TableHead>
								<TableHead className="text-center">5 วัน</TableHead>
								<TableHead className="text-center">7 วัน</TableHead>
								<TableHead className="text-center">14 วัน</TableHead>
								<TableHead className="text-center">30 วัน</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{/* Deposits Row */}
							<TableRow className="bg-green-50 dark:bg-green-950/20">
								<TableCell className="font-semibold">
									<div className="flex items-center gap-2">
										<TrendingUp className="h-4 w-4 text-green-600" />
										<span>ยอดฝาก</span>
									</div>
								</TableCell>
								{(
									['day1', 'day3', 'day5', 'day7', 'day14', 'day30'] as const
								).map((period) => (
									<TableCell key={period} className="text-center">
										<div className="flex flex-col">
											<span className="font-bold text-green-600 dark:text-green-400">
												{formatConversionRate(
													analytics.depositConversion[period].rate,
												)}
											</span>
											<span className="text-xs text-muted-foreground">
												{formatConversionAmount(
													analytics.depositConversion[period].amount,
												)}
											</span>
										</div>
									</TableCell>
								))}
							</TableRow>

							{/* Withdrawals Row */}
							<TableRow className="bg-rose-50 dark:bg-rose-950/20">
								<TableCell className="font-semibold">
									<div className="flex items-center gap-2">
										<TrendingDown className="h-4 w-4 text-rose-600" />
										<span>ยอดถอน</span>
									</div>
								</TableCell>
								{(
									['day1', 'day3', 'day5', 'day7', 'day14', 'day30'] as const
								).map((period) => (
									<TableCell key={period} className="text-center">
										<div className="flex flex-col">
											<span className="font-bold text-rose-600 dark:text-rose-400">
												{formatConversionRate(
													analytics.withdrawConversion[period].rate,
												)}
											</span>
											<span className="text-xs text-muted-foreground">
												{formatConversionAmount(
													analytics.withdrawConversion[period].amount,
												)}
											</span>
										</div>
									</TableCell>
								))}
							</TableRow>
						</TableBody>
					</Table>
				</div>
			</CardContent>
		</Card>
	)
}
